import React from 'react';

/**
 * High-performance virtual scrolling engine
 * Handles 1000+ items without pagination using window virtualization
 */

export class VirtualScrollEngine {
  constructor(options = {}) {
    this.itemHeight = options.itemHeight || 52;
    this.containerHeight = options.containerHeight || 600;
    this.buffer = options.buffer || 5; // Extra items to render for smooth scrolling
    this.scrollTop = 0;
    this.data = [];

    // Calculate initial values
    this.visibleCount = Math.ceil(this.containerHeight / this.itemHeight);
    this.totalHeight = 0;
    this.startIndex = 0;
    this.endIndex = 0;
  }

  setData(data) {
    this.data = data;
    this.totalHeight = data.length * this.itemHeight;
    this.updateVisibleRange();
  }

  setScrollTop(scrollTop) {
    this.scrollTop = Math.max(0, Math.min(scrollTop, this.getMaxScrollTop()));
    this.updateVisibleRange();
  }

  updateVisibleRange() {
    // Calculate which items should be visible
    const startIndex = Math.floor(this.scrollTop / this.itemHeight);
    const endIndex = Math.min(
      startIndex + this.visibleCount + this.buffer,
      this.data.length
    );

    // Add buffer for smooth scrolling
    this.startIndex = Math.max(0, startIndex - this.buffer);
    this.endIndex = endIndex;
  }

  getVisibleItems() {
    return this.data.slice(this.startIndex, this.endIndex).map((item, index) => ({
      ...item,
      virtualIndex: this.startIndex + index,
      top: (this.startIndex + index) * this.itemHeight
    }));
  }

  getMaxScrollTop() {
    return Math.max(0, this.totalHeight - this.containerHeight);
  }

  getScrollbarHeight() {
    if (this.totalHeight <= this.containerHeight) return this.containerHeight;
    return Math.max(20, (this.containerHeight / this.totalHeight) * this.containerHeight);
  }

  getScrollbarTop() {
    const maxScrollTop = this.getMaxScrollTop();
    if (maxScrollTop === 0) return 0;

    const scrollbarTrackHeight = this.containerHeight - this.getScrollbarHeight();
    return (this.scrollTop / maxScrollTop) * scrollbarTrackHeight;
  }

  // Calculate item index from Y position
  getItemIndexFromPosition(y) {
    return Math.floor(y / this.itemHeight);
  }

  // Get item position from index
  getItemPosition(index) {
    return index * this.itemHeight;
  }

  // Smooth scroll to item
  scrollToItem(index, position = 'top') {
    const itemTop = this.getItemPosition(index);
    let targetScrollTop;

    switch (position) {
      case 'top':
        targetScrollTop = itemTop;
        break;
      case 'center':
        targetScrollTop = itemTop - (this.containerHeight - this.itemHeight) / 2;
        break;
      case 'bottom':
        targetScrollTop = itemTop - this.containerHeight + this.itemHeight;
        break;
      default:
        targetScrollTop = itemTop;
    }

    this.setScrollTop(targetScrollTop);
    return targetScrollTop;
  }

  // Performance metrics
  getPerformanceMetrics() {
    return {
      totalItems: this.data.length,
      visibleItems: this.endIndex - this.startIndex,
      renderRatio: ((this.endIndex - this.startIndex) / this.data.length * 100).toFixed(1) + '%',
      scrollPosition: Math.round((this.scrollTop / this.getMaxScrollTop()) * 100) + '%',
      memoryEfficiency: `${this.data.length} items, ${this.endIndex - this.startIndex} rendered`
    };
  }
}

// Hook for easy React integration
export const useVirtualScroll = (data, options = {}) => {
  const engineRef = React.useRef(new VirtualScrollEngine(options));
  const [visibleItems, setVisibleItems] = React.useState([]);
  const [scrollTop, setScrollTop] = React.useState(0);

  const engine = engineRef.current;

  // Update data
  React.useEffect(() => {
    engine.setData(data || []);
    setVisibleItems(engine.getVisibleItems());
  }, [data, engine]);

  // Handle scroll
  const handleScroll = React.useCallback((newScrollTop) => {
    engine.setScrollTop(newScrollTop);
    setScrollTop(newScrollTop);
    setVisibleItems(engine.getVisibleItems());
  }, [engine]);

  // Scroll to item
  const scrollToItem = React.useCallback((index, position) => {
    const targetScrollTop = engine.scrollToItem(index, position);
    setScrollTop(targetScrollTop);
    setVisibleItems(engine.getVisibleItems());
    return targetScrollTop;
  }, [engine]);

  return {
    visibleItems,
    scrollTop,
    totalHeight: engine.totalHeight,
    handleScroll,
    scrollToItem,
    startIndex: engine.startIndex,
    endIndex: engine.endIndex,
    metrics: engine.getPerformanceMetrics()
  };
};